import { useMemo, useState } from 'react'
import { useLanguage } from '../hooks/useLanguage'

interface TokenFollowManagerProps {
  symbols: string[]
  followedTokens: string[]
  maxCount: number
  busyToken?: string
  onFollow: (token: string) => Promise<void>
  onUnfollow: (token: string) => Promise<void>
}

export function TokenFollowManager({
  symbols,
  followedTokens,
  maxCount,
  busyToken,
  onFollow,
  onUnfollow,
}: TokenFollowManagerProps) {
  const { t } = useLanguage()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const canonicalToken = useMemo(
    () => symbols.find((symbol) => symbol.toUpperCase() === query.trim().toUpperCase()),
    [query, symbols],
  )
  const suggestions = useMemo(() => {
    const normalized = query.trim().toUpperCase()
    if (!normalized) return symbols.slice(0, 8)
    return symbols.filter((symbol) => symbol.toUpperCase().includes(normalized)).slice(0, 8)
  }, [query, symbols])
  const isNewTokenAtLimit = Boolean(
    canonicalToken
    && !followedTokens.includes(canonicalToken)
    && followedTokens.length >= maxCount,
  )

  const select = (token: string) => {
    setQuery(token)
    setOpen(false)
  }

  const submit = async () => {
    if (!canonicalToken || busyToken) return
    if (followedTokens.includes(canonicalToken)) {
      await onUnfollow(canonicalToken)
    } else {
      await onFollow(canonicalToken)
    }
    setQuery('')
    setOpen(false)
  }

  return (
    <div className="space-y-3">
      <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('关注 Token', 'Follow Token')}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {t('搜索白名单内的 Token，关注后会显示在下方。', 'Search an available token and follow it.')}
          </p>
        </div>

        <div className="relative">
          <div className={`flex h-11 items-center gap-2 rounded-xl border bg-gray-50 px-3 transition-colors dark:bg-gray-800 ${
            open ? 'border-blue-400 ring-2 ring-blue-500/10' : 'border-gray-200 dark:border-gray-700'
          }`}>
            <span className="text-gray-400" aria-hidden="true">⌕</span>
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value.toUpperCase())
                setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => window.setTimeout(() => setOpen(false), 120)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  if (canonicalToken) void submit()
                  else if (suggestions[0]) select(suggestions[0])
                }
                if (event.key === 'Escape') setOpen(false)
              }}
              placeholder={t('搜索 Token，例如 BTC、ETH', 'Search token, e.g. BTC, ETH')}
              className="min-w-0 flex-1 bg-transparent text-sm font-medium uppercase text-gray-900 outline-none placeholder:normal-case placeholder:text-gray-400 dark:text-white"
            />
            <button
              type="button"
              disabled={!canonicalToken || Boolean(busyToken) || isNewTokenAtLimit}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void submit()}
              className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600"
            >
              {isNewTokenAtLimit
                ? t('已达上限', 'Limit reached')
                : canonicalToken && followedTokens.includes(canonicalToken)
                ? t('取消关注', 'Unfollow')
                : t('关注', 'Follow')}
            </button>
          </div>

          {open && (
            <div className="absolute left-0 right-0 top-full z-30 mt-1.5 rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900">
              <div className="mb-2 flex items-center gap-2 px-1">
                <span className="text-xs text-gray-400">{t('快速选择', 'Quick select')}</span>
                {['BTC', 'ETH'].map((token) => (
                  <button
                    key={token}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => select(token)}
                    className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700 hover:bg-blue-100 hover:text-blue-700 dark:bg-gray-800 dark:text-gray-200"
                  >
                    {token}
                  </button>
                ))}
              </div>
              <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
                {suggestions.map((token) => (
                  <button
                    key={token}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => select(token)}
                    className="rounded-md px-2.5 py-1.5 text-sm font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-700 dark:text-gray-200 dark:hover:bg-blue-950/50 dark:hover:text-blue-300"
                  >
                    {token}
                    {followedTokens.includes(token) && (
                      <span className="ml-1 text-[10px] font-normal text-blue-500">✓</span>
                    )}
                  </button>
                ))}
                {suggestions.length === 0 && (
                  <span className="px-2 py-1 text-sm text-gray-400">
                    {t('没有匹配的 Token', 'No matching token')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('已关注 Token', 'Followed Tokens')}
          </h2>
          <span className="text-xs text-gray-400">{followedTokens.length}/{maxCount}</span>
        </div>
        {followedTokens.length === 0 ? (
          <p className="py-2 text-center text-xs text-gray-500 dark:text-gray-400">
            {t('暂未关注 Token', 'No followed tokens yet')}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {followedTokens.map((token) => (
              <div
                key={token}
                className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-800"
              >
                <span className="text-sm font-bold text-gray-900 dark:text-white">{token}</span>
                <button
                  type="button"
                  disabled={Boolean(busyToken)}
                  onClick={() => void onUnfollow(token)}
                  className="text-xs text-gray-400 hover:text-red-500 disabled:opacity-50"
                  aria-label={t(`取消关注 ${token}`, `Unfollow ${token}`)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
